import { last } from 'lodash'
import semver from 'semver'

export function getCanonicalFamilyStyle(icon) {
  const isClassicDuotoneIcon = icon.family === 'classic' && icon.style === 'duotone'
  return isClassicDuotoneIcon ? { family: 'duotone', style: 'solid' } : { family: icon.family, style: icon.style }
}

export function hasIconFamilyStyleAccess(params) {
  const { icon, permits, route, iconReleasedVersion, activeRelease, isCustomIcon } = params

  let iconVersionToCheck = iconReleasedVersion

  // Custom icons bypass version constraints
  if (isCustomIcon) {
    return true
  }

  // If no icon version is provided, we can't perform version checks
  if (!iconVersionToCheck) {
    return false
  }

  // Get user's access level and check icon family/style access
  const lastProVersion = last(permits.get('accessToPro', 'versions'))
  const lastProPlusVersion = last(permits.get('accessToProPlus', 'versions'))

  // Determine the user's last accessible release, prioritizing Pro+ access, then Pro access, and finally falling
  // back to the active release if neither exists (a free user)
  const userLastAccessibleRelease = lastProPlusVersion || lastProVersion || activeRelease

  if (!semver.valid(iconVersionToCheck)) {
    try {
      const coercedIconReleasedVersion = semver.coerce(iconVersionToCheck)

      if (coercedIconReleasedVersion && semver.valid(coercedIconReleasedVersion)) {
        iconVersionToCheck = coercedIconReleasedVersion
      } else {
        console.error(`Invalid semver string: ${iconVersionToCheck}`)
        return false
      }
    } catch (e) {
      console.error(`Invalid semver string: ${iconVersionToCheck}`, e)
      return false
    }
  }

  // check iconVersionToCheck against userLastAccessibleRelease
  if (semver.gt(iconVersionToCheck, userLastAccessibleRelease.version)) return false

  // check userLastAccessibleRelease major version against route.meta major version
  if (semver.major(userLastAccessibleRelease.version) < semver.major(route.meta.version)) return false

  const userFamilyStyles = userLastAccessibleRelease?.familyStyles || []

  const { family, style } = getCanonicalFamilyStyle(icon)

  return userFamilyStyles.some((fs) => fs.family === family && fs.style === style)
}

export function canAccessAndDownloadSvg(params) {
  const { icon, permits, route, isCustomIcon } = params

  // Early exits for simple cases
  if (!icon) return false
  if (icon.is_free) return true
  if (!route.meta.isSignedIn) return false
  if (isCustomIcon && permits.has('accessToProDownload')) return true

  // Check icon family/style access and version constraints
  const hasAccess = hasIconFamilyStyleAccess(params)

  return hasAccess && permits?.has('accessToProDownload')
}

export function canUseIcon(params) {
  const { icon, isCustomIcon, permits, route } = params

  if (isCustomIcon) return true
  if (icon.is_free) return true
  if (!route.meta.isSignedIn) return false

  // Check icon family/style access and version constraints
  const hasAccess = hasIconFamilyStyleAccess(params)

  return hasAccess && permits?.has('accessToPro')
}

export function isProPlusIcon(icon) {
  return icon?.primary_collection === 'pro-plus-collection'
}
